import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../../firebase';
import { format } from 'date-fns';
import ClientLayout from '../../../components/layout/ClientLayout';
import { useEmailVerification } from '../../../hooks/useEmailVerification';

interface Reservation {
  id: string;
  facilityId?: string;
  facilityName: string;
  reservationDate: Date;
  startTime: number;
  endTime: number;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  createdAt: Date;
  price?: number;
  notice?: string;
}

const FacilityReservationListPage: React.FC = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [facilityNotices, setFacilityNotices] = useState<Record<string, string>>({});
  const facilityNoticesRef = useRef<Record<string, string>>({});
  const { loading: emailVerificationLoading } = useEmailVerification();

  useEffect(() => {
    facilityNoticesRef.current = facilityNotices;
    setReservations(prev =>
      prev.map(reservation => ({
        ...reservation,
        notice: reservation.facilityId ? (facilityNotices[reservation.facilityId] || '') : ''
      }))
    );
  }, [facilityNotices]);

  // 사용자 인증 상태 확인
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((authUser) => {
      if (!authUser) {
        navigate('/signin');
      } else {
        setCurrentUser(authUser);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // 예약 내역 가져오기
  useEffect(() => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    const reservationsRef = collection(db, 'reservations');
    const q = query(
      reservationsRef,
      where('uid', '==', currentUser.uid),
      orderBy('reservationDate', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const nextReservations = querySnapshot.docs.map((docSnapshot) => {
          const data = docSnapshot.data();
          const facilityId = data.facilityId || '';

          return {
            id: docSnapshot.id,
            facilityId,
            facilityName: data.facilityName || '',
            reservationDate: data.reservationDate?.toDate ? data.reservationDate.toDate() : new Date(),
            startTime: data.startTime || 0,
            endTime: data.endTime || 0,
            status: data.status || 'pending',
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            price: data.price || 0,
            notice: facilityId ? (facilityNoticesRef.current[facilityId] || '') : ''
          };
        }) as Reservation[];

        setReservations(nextReservations);
        setLoading(false);

        // 시설물 notice는 변경 빈도가 낮아 캐싱해서 가져옴
        const missingFacilityIds = Array.from(
          new Set(
            querySnapshot.docs
              .map(d => (d.data().facilityId || '') as string)
              .filter(fid => fid && !(fid in facilityNoticesRef.current))
          )
        );

        if (missingFacilityIds.length > 0) {
          missingFacilityIds.forEach(async (facilityId) => {
            try {
              const facilityDoc = await getDoc(doc(db, 'facilities', facilityId));
              if (!facilityDoc.exists()) return;
              const notice = facilityDoc.data().notice || '';
              setFacilityNotices(prev => (prev[facilityId] === notice ? prev : { ...prev, [facilityId]: notice }));
            } catch (err) {
              console.error('시설물 정보를 불러오는 중 오류:', err);
            }
          });
        }
      },
      (err) => {
        console.error('예약 내역을 불러오는 중 오류가 발생했습니다:', err);
        setError('예약 내역을 불러오는 중 오류가 발생했습니다.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return '대기중';
      case 'approved':
        return '승인됨';
      case 'rejected':
        return '거절됨';
      case 'cancelled':
        return '취소됨';
      default:
        return status;
    }
  };

  if (loading || emailVerificationLoading) {
    return (
      <ClientLayout>
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">예약 내역을 불러오는 중...</p>
            </div>
          </div>
        </div>
      </ClientLayout>
    );
  }

  if (error) {
    return (
      <ClientLayout>
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="w-full min-h-screen bg-white">
        <div className="w-full max-w-[760px] mx-auto relative pt-8 px-4 sm:px-6 lg:px-8">
          {/* 헤더 섹션 */}
          <div className="mt-8">
            <div className="text-slate-800 text-2xl sm:text-3xl lg:text-4xl font-extrabold font-['Pretendard'] leading-tight">
              예약 내역
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="text-slate-700 text-lg sm:text-xl font-normal font-['Pretendard'] leading-tight">
                예약 내역을 확인해주세요
              </div>
              <button
                onClick={() => navigate('/facility')}
                className="w-full sm:w-44 h-14 sm:h-16 bg-blue-500 hover:bg-blue-600 rounded-sm"
              >
                <div className="text-center text-white text-base sm:text-lg font-bold font-['Pretendard'] leading-tight">
                  새 예약하기
                </div>
              </button>
            </div>
          </div>

          {/* 구분선 */}
          <div className="w-full h-px mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>

          <div className="mt-8">
            {reservations.length === 0 ? (
              <div className="bg-white rounded border border-black/5 p-6 sm:p-12 text-center">
                <p className="text-slate-500 text-base sm:text-lg font-medium font-['Pretendard']">예약 내역이 없습니다.</p>
                <button
                  onClick={() => navigate('/facility')}
                  className="mt-6 text-blue-600 hover:text-blue-800 text-sm sm:text-base font-bold font-['Pretendard']"
                >
                  새 예약하기
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {reservations.map((reservation) => (
                  <div
                    key={reservation.id}
                    className="p-4 sm:p-6 rounded-sm outline outline-[0.50px] bg-white cursor-pointer transition-colors hover:outline-blue-300 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3"
                    onClick={() => navigate(`/facility/reservations/${reservation.id}`)}
                  >
                    <div className="flex-1">
                      <div className="text-slate-800 text-base sm:text-lg font-bold font-['Pretendard'] mb-1">
                        {reservation.facilityName}
                      </div>
                      <div className="text-slate-700 text-sm sm:text-base font-normal font-['Pretendard']">
                        {format(reservation.reservationDate, 'yyyy년 MM월 dd일')} {reservation.startTime}시 - {reservation.endTime}시
                      </div>
                      <div className="text-blue-600 text-sm sm:text-base font-bold font-['Pretendard'] mt-1">
                        {(reservation.price || 0).toLocaleString()}원
                      </div>
                      {reservation.notice && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded">
                          <div className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div className="text-amber-800 text-xs sm:text-sm font-normal font-['Pretendard']">
                              {reservation.notice}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="self-start sm:self-center">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm font-bold font-['Pretendard'] ${getStatusColor(reservation.status)}`}>
                        {getStatusText(reservation.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 하단 여백 */}
          <div className="h-16 sm:h-32"></div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default FacilityReservationListPage; 
